import { FC, useState } from "react";
import { useToggle } from "rooks";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { EditOutlined } from "@mui/icons-material";
import { ReFolderWithChildren } from "@/components/pages/billing-explorer-all-views/components/table/utils/types/types";
import { useAppAbility } from "@/services/permissions";
import { useAGGridRowHoverHook } from "@/components/pages/common/ag-grid/utils/hooks/useAGGridRowHover.hook";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { BillingExplorerFolderMoveAction } from "@/components/app-navigation/components/page-components/billing-explorer/components/folder-actions-menu/move/BillingExplorerFolderMoveAction";
import { BillingExplorerFolderAddReport } from "@/components/app-navigation/components/page-components/billing-explorer/components/folder-actions-menu/BillingExplorerFolderAddReport";
import { BillingExplorerFolderItemNameEdit } from "@/components/pages/billing-explorer-all-views/components/table/components/cells/BillingExplorerFolderItemNameEdit";

interface BillingExplorerFolderNameCellProps {
  data: ReFolderWithChildren;
}

export const BillingExplorerFolderNameCell: FC<
  BillingExplorerFolderNameCellProps
> = ({ data }) => {
  const { visibility, name, items } = data ?? {};
  const folderItemCount = items?.length;
  const { can } = useAppAbility();
  const canEditReport = can("edit", "resource-explorer");

  const { ref, hovered } = useAGGridRowHoverHook();
  const [forceHover, setForceHover] = useState(false);
  const [editName, toggleEditName] = useToggle(false);

  const openHandler = () => setForceHover(true);
  const closeHandler = () => setForceHover(false);

  if (editName) {
    return (
      <BillingExplorerFolderItemNameEdit
        folder
        name={data.name}
        id={data.id}
        onSubmit={toggleEditName}
      />
    );
  }

  return (
    <Stack
      ref={ref}
      spacing={1}
      direction="row"
      alignItems="center"
      sx={{
        cursor: "pointer",
      }}
    >
      {visibility === "visible_only_to_me" && (
        <LockOutlinedIcon color="action" sx={{ fontSize: 16 }} />
      )}

      <TypographyWithTooltip variant="inherit" title={name} />

      {folderItemCount !== undefined && (
        <Stack
          alignItems="center"
          width={22}
          height={22}
          minWidth={22}
          minHeight={22}
          borderRadius={10}
          justifyContent="center"
          bgcolor="grey.200"
        >
          <Typography color="inherit" fontWeight={500} fontSize={12}>
            {folderItemCount}
          </Typography>
        </Stack>
      )}

      {(hovered || forceHover) && canEditReport && (
        <Stack direction="row" spacing={1}>
          <Tooltip arrow title="Edit Name" placement="top">
            <Box>
              <IconButton
                size="small"
                sx={iconButtonStyles}
                onClick={toggleEditName}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>

          <BillingExplorerFolderMoveAction
            iconButton
            id={data?.id ?? ""}
            name={data?.name ?? ""}
            visibility={data?.visibility ?? "visible_to_everyone"}
            onClose={closeHandler}
            onOpen={openHandler}
            containsProtectedItem={data.items?.some(
              (item) => item.protection_details?.is_protected,
            )}
          />

          <BillingExplorerFolderAddReport
            iconButon
            id={data?.id ?? ""}
            onOpen={openHandler}
            onClose={closeHandler}
            visibility={data?.visibility ?? "visible_to_everyone"}
          />
        </Stack>
      )}
    </Stack>
  );
};

const iconButtonStyles = { p: 0.3, "&:hover": { bgcolor: "grey.200" } };
